<template>
    <div>
        <section class="campaigns-table">
            <DsTableGrid
                ref="table"
                v-model="selectedCampaignIndices"
                :data="formattedListings"
                :show-loading="isLoading"
                :sort-field="sortField"
                :sort-ascending="sortAscending"
                :selected-options="archived ? buttonTrayArchived : buttonTrayActive"
                :selected-item-label="selectedItemLabel"
                :infinite="!error && routePath === '/campaigns/marketplace'"
                :selectable="true"
                :click-row="sendToCampaignDetailsPage"
                @action="handleTrowserAction"
                @load="loadMoreCampaigns"
                @sort="sort"
            >
                <DsTableGridColumn
                    :label="$t('campaignsPage.table.name')"
                    :width="columnWidths[0]"
                    sortable
                    prop="name"
                >
                    <template #default="props">
                        <router-link
                            :to="routerLinkToCampaignDetailsPage(props)"
                            :data-qa="`${props.name}`"
                        >
                            {{ props.name }}
                        </router-link>
                    </template>
                </DsTableGridColumn>

                <DsTableGridColumn
                    :label="$t('campaignsPage.table.exported')"
                    prop="dateCreated"
                    :width="columnWidths[1]"
                    sortable
                >
                    <template #default="props">
                        <DsTooltip
                            position="top"
                            :class="tooltipsClass(props.dateCreated)"
                        >
                            <template #reference>
                                {{ timeAgo(props.dateCreated, 'MMM D YYYY') }}
                            </template>
                            <div>{{ tooltipDateFormat(props.dateCreated) }}</div>
                        </DsTooltip>
                    </template>
                </DsTableGridColumn>

                <DsTableGridColumn
                    :label="$t('campaignsPage.table.updated')"
                    prop="releaseDate"
                    :width="columnWidths[2]"
                    sortable
                >
                    <template #default="props">
                        <DsTooltip
                            position="top"
                            :class="tooltipsClass(props.releaseDate)"
                        >
                            <template #reference>
                                {{ timeAgo(props.releaseDate, 'MMM D YYYY') }}
                            </template>
                            <div>{{ tooltipDateFormat(props.releaseDate) }}</div>
                        </DsTooltip>
                    </template>
                </DsTableGridColumn>

                <DsTableGridColumn
                    :label="$t('campaignsPage.table.installCount')"
                    prop="installCount"
                    :width="columnWidths[3]"
                />

                <DsTableGridColumn
                    :label="$t('campaignsPage.table.published')"
                    prop="publishedCount"
                    :width="columnWidths[4]"
                />

                <DsTableGridColumn
                    :label="$t('campaignsPage.table.active')"
                    prop="totalWithActiveContacts"
                    :width="columnWidths[5]"
                >
                    <template #default="props">
                        <div class="active-column-value">
                            <span class="active-column-number">
                                {{ shortNumber(props.installCountsWithActive) }}
                            </span>

                            <stat-pill
                                :color="props.activeStatus"
                                :value="props.activePercentage"
                            />
                        </div>
                    </template>
                </DsTableGridColumn>
            </DsTableGrid>

            <empty-state-box
                v-if="state"
                page="campaignsPage"
                :state="state"
                :has-action="Boolean(stateAction)"
                @buttonEvent="stateAction ? stateAction : () => {}"
            />
        </section>

        <bundle-install-modal
            :title="$t('campaignsPage.bundles.installModal.titleCampaign')"
            :bundle="campaignsToInstall"
            :is-open="isBundleInstallModalOpen"
            @close="isBundleInstallModalOpen = false"
            @campaigns-installing="openMarketplaceModal"
        />

        <marketplace-link-modal
            :is-open="isMarketplaceLinkModalOpen"
            @close="isMarketplaceLinkModalOpen = false"
        />

        <campaigns-archive-warning
            :is-open="isCampaignsArchiveWarningOpen"
            :campaigns-in-bundles="selectedCampaignsInBundles"
            @close="isCampaignsArchiveWarningOpen = false"
            @archive="archiveOrUnarchiveCampaigns"
        />
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import debounce from 'lodash.debounce';
import shortNumber from 'short-number';
import moment from 'moment';

import dateUtils from '@/mixins/date-utils';

import BundleInstallModal from 'pages/bundles/BundlesPage/components/BundleInstallModal';
import MarketplaceLinkModal from 'pages/bundles/BundlesPage/components/MarketplaceLinkModal';
import CampaignsArchiveWarning from 'pages/campaigns/CampaignsPage/components/CampaignsArchiveWarning';
import CreateBundle from 'pages/bundles/BundlesPage/components/CreateBundle';
import EmptyStateBox from 'components/EmptyState/EmptyStateBox';
import StatPill from 'components/StatPill/StatPill';

export default {
    components: {
        BundleInstallModal,
        MarketplaceLinkModal,
        CampaignsArchiveWarning,
        EmptyStateBox,
        StatPill,
    },

    mixins: [dateUtils],

    props: {
        searchText: {
            type: String,
            default: '',
        },
        archived: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            buttonTrayActive: [
                {
                    label: this.$t('campaignsPage.bundles.install'),
                    className: 'secondary',
                    value: 'installCampaign',
                },
                {
                    label: this.$t('campaignsPage.createBundle.title'),
                    className: 'secondary',
                    value: 'createBundle',
                },
                {
                    label: this.$t('campaignsPage.table.actions.archive.title'),
                    className: 'secondary',
                    value: 'tryArchiveOrUnarchiveCampaigns',
                },
            ],
            buttonTrayArchived: [
                {
                    label: this.$t('campaignsPage.table.actions.unarchive.title'),
                    className: 'secondary',
                    value: 'tryArchiveOrUnarchiveCampaigns',
                },
            ],
            campaignsToInstall: { solutions: [] },
            columnWidths: ['290', '110', '110', '100', '90', '125'],
            pageNumber: 0,
            pageSize: 15,
            searchTextDebounce: 500,
            selectedCampaignIndices: [],
            sortAscending: false,
            sortField: 'releaseDate',
            totalListings: Number,
            isBundleInstallModalOpen: false,
            isMarketplaceLinkModalOpen: false,
            isCampaignsArchiveWarningOpen: false,
        };
    },

    mounted() {
        /* istanbul ignore next */
        this.$bus.$on('RELOAD_CAMPAIGNS', () => {
            this.loadCampaigns();
        });
    },

    created() {
        this.refreshListings = debounce(this.refreshListings, this.searchTextDebounce);
        this.sort = debounce(this.sort, 300);

        /* istanbul ignore next */
        return this.loadCampaigns();
    },

    beforeDestroy() {
        /* istanbul ignore next */
        this.$bus.$off('RELOAD_CAMPAIGNS');
    },

    watch: {
        searchText() {
            this.selectedCampaignIndices = [];
            this.refreshListings();
        },

        archived() {
            this.loadCampaigns();
        },

        listings() {
            (this.listings.length === this.meta.totalElements) ? this.$refs.table.noMoreData = true : this.$refs.table.noMoreData = false;
        },

        isLoading: {
            handler(isLoading) {
                if (isLoading) {
                    this.$emit('dataLoading');
                }
            },
            immediate: true,
        },
    },

    computed: {
        ...mapState({
            listings: ({ api }) => api.campaigns.fetch.entities,
            error: ({ api }) => api.campaigns.fetch.error,
            meta: ({ api }) => api.campaigns.fetch.meta,
            isLoading: ({ api }) => api.campaigns.fetch.isLoading,
            routePath: ({ route }) => route.path,
            areCampaignsInBundles: ({ api }) => api.campaigns.fetch.bundlesWithWarning.length > 0,
            archiveWarningEnabled: ({ featureFlags }) => featureFlags.flags['optimus.archiveCampaignsWarning'],
        }),

        ...mapGetters({
            campaignIdsInBundles: 'api/campaigns/campaignIdsInBundles',
        }),

        state() {
            if (this.error === 'No publisherCompanyIds') {
                return 'emptyListingState';
            }

            if (this.listings.length === 0 && !this.isLoading) {
                const emptyFilter = this.searchText !== '';

                return (emptyFilter) ? 'emptyFilterState' : 'emptyListingState';
            }

            return null;
        },

        stateAction() {
            if (this.state === 'emptyFilterState') {
                return this.resetFilters;
            }

            return null;
        },

        formattedListings() {
            return this.listings.map(this.formatListing);
        },

        selectedItemLabel() {
            return this.$tc('campaignsPage.createBundle.campaignsSelected', this.selectedCampaignIds.length);
        },

        selectedCampaignIds () {
            return this.selectedCampaignIndices.map((index) => this.listings[index].immutableId);
        },

        selectedCampaignsInBundles() {
            const selectedCampaignsInBundles = [];

            this.selectedCampaignIndices.forEach((index) => {
                if (this.campaignIdsInBundles.includes(this.listings[index].immutableId)) {
                    selectedCampaignsInBundles.push(this.listings[index].name);
                }
            });

            return selectedCampaignsInBundles;
        },
    },

    methods: {

        async tryArchiveOrUnarchiveCampaigns() {
            if (!this.archived && this.archiveWarningEnabled) {
                await this.$store.dispatch('api/campaigns/FETCH_CAMPAIGNS_BUNDLE_WARNING', { campaignIds: this.selectedCampaignIds });

                if (this.areCampaignsInBundles) {
                    this.isCampaignsArchiveWarningOpen = true;

                    return;
                }
            }
            this.archiveOrUnarchiveCampaigns();
        },

        async archiveOrUnarchiveCampaigns() {
            const action = this.archived ? 'UNARCHIVE_LISTINGS' : 'ARCHIVE_LISTINGS';

            try {
                await this.$store.dispatch(`automations/${action}`, this.selectedCampaignIds);
                const ids = [...this.selectedCampaignIds];
                const selectedCampaignNames = [];

                this.selectedCampaignIndices.forEach((i) => {
                    const campaign = {
                        name: this.listings[i].name,
                    };

                    selectedCampaignNames.push(campaign.name);
                });

                if (selectedCampaignNames.length > 1 && action === 'ARCHIVE_LISTINGS') {
                    this.$toast({
                        headline: this.$t('campaignArchive'),
                        message: this.$t(`${selectedCampaignNames.length} campaigns were archived successfully.`),
                        action: () => {
                            this.undoArchiveOrUnarchiveCampaigns(ids);
                        },
                        actionText: this.$t('undo'),
                    });
                } else if (selectedCampaignNames.length === 1 && action === 'ARCHIVE_LISTINGS') {
                    this.$toast({
                        headline: this.$t('campaignArchive'),
                        message: this.$t(`${selectedCampaignNames} was archived successfully.`),
                        action: () => {
                            this.undoArchiveOrUnarchiveCampaigns(ids);
                        },
                        actionText: this.$t('undo'),
                    });
                }
            } catch (e) {
                this.$error({
                    message: this.$t('campaignsPage.archiveAndUnarchiveError'),
                });
            }

            this.selectedCampaignIndices = [];
            this.$refs.table.handleSelectAll(false);
            this.$store.commit('api/campaigns/FETCH_CAMPAIGNS_PENDING');

            await this.loadCampaigns();
        },

        async undoArchiveOrUnarchiveCampaigns(ids) {
            const action = this.archived ? 'ARCHIVE_LISTINGS' : 'UNARCHIVE_LISTINGS';

            await this.$store.dispatch(`automations/${action}`, ids);

            this.selectedCampaignIndices = [];
            this.$refs.table.handleSelectAll(false);
            this.$store.commit('api/campaigns/FETCH_CAMPAIGNS_PENDING');

            await this.loadCampaigns();
        },

        openMarketplaceModal() {
            this.isMarketplaceLinkModalOpen = true;
        },

        loadCampaigns() {
            const sortDirection = (this.sortAscending ? 'ASC' : 'DESC');
            const sortField = (this.sortField === 'releaseDate') ? 'stats.releaseDate' : this.sortField;

            return this.$store.dispatch('api/campaigns/FETCH', {
                params: {
                    searchText: this.searchText,
                    archived: this.archived,
                    sort: `${sortField}:${sortDirection}`,
                },
            }).then((resultCount) => {
                this.$emit('dataLoaded', { resultCount });
            });
        },

        loadMoreCampaigns() {
            if (this.error || this.isLoading) {
                return Promise.resolve();
            }

            return this.$store.dispatch('api/campaigns/FETCH_MORE').then((resultCount) => {
                this.$emit('dataLoaded', { resultCount });
            });
        },

        createBundle() {
            const bundleCampaigns = [];

            this.selectedCampaignIndices.forEach((i) => {
                const campaign = {
                    id: this.listings[i].immutableId,
                    name: this.listings[i].name,
                };

                bundleCampaigns.push(campaign);
            });

            this.$bus.$emit('PUSH_NESTED_MODAL', {
                component: CreateBundle,
                data: bundleCampaigns,
                showRootClose: true,
            });

            this.selectedCampaignIndices = [];
            this.$refs.table.handleSelectAll(false);
        },

        formatListing(listing) {
            const {
                name,
                /* istanbul ignore next */
                installCount = 0,
                /* istanbul ignore next */
                publishedCount = 0,
                /* istanbul ignore next */
                installCountsWith: { active: installCountsWithActive = 0 },
                dateCreated,
                releaseDate,
                immutableId,
            } = listing;

            const activePercentage = (publishedCount > 0 && installCount > 0)
                ? Math.round(installCountsWithActive / publishedCount * 100)
                : 0;

            const activeStatus = this.getActiveColor(activePercentage);

            return {
                name,
                installCount,
                publishedCount,
                installCountsWithActive,
                immutableId,
                dateCreated: moment(dateCreated).valueOf(),
                releaseDate: moment(releaseDate).valueOf(),
                activePercentage: `${activePercentage}%`,
                activeStatus,
            };
        },

        getActiveColor(percent) {
            if (percent < 15) {
                return 'red';
            }

            return percent >= 35 ? 'green' : 'yellow';
        },

        handleTrowserAction(action) {
            if (action) {
                this[action]();
            }
        },

        installCampaign() {
            const bundleCampaigns = [];

            this.selectedCampaignIndices.forEach((i) => {
                const selectedListing = this.listings[i];

                bundleCampaigns.push({ ...selectedListing });
            });

            this.campaignsToInstall.solutions = bundleCampaigns;
            this.selectedCampaignIndices = [];
            this.$refs.table.handleSelectAll(false);
            this.isBundleInstallModalOpen = true;
        },

        refreshListings() {
            return this.loadCampaigns();
        },

        resetFilters() {
            this.$emit('resetFilters');
        },

        shortNumber(value) {
            return value ? shortNumber(value) : 0;
        },

        resetListings() {
            this.pageNumber = 0;
            this.$refs.table.noMoreData = false;
        },

        sort({ field, ascending }) {
            if (this.sortField !== field || this.sortAscending !== ascending) {
                this.sortField = field;
                this.sortAscending = ascending;
                this.loadCampaigns();
            }
        },

        tooltipsClass(date) {
            return this.isLessThanSevenDays(date) ? 'date-tooltip-wide' : 'date-tooltip-narrow';
        },

        sendToCampaignDetailsPage(props) {
            this.$router.push(this.routerLinkToCampaignDetailsPage(props));
        },

        routerLinkToCampaignDetailsPage(props) {
            return {
                name: 'campaign',
                params: {
                    id: props.immutableId,
                },
            };
        },
    },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
    @import "~@/styles/table";

    .campaigns {
        overflow-x: auto;
        overflow-y: hidden;
        margin: 0;

        .active-column-value {
            display: flex;
        }

        .active-column-number {
            flex: 0 0 45%;
            text-align: right;
            padding: 0 $spacing-100 0 0;
        }

        .disabled-link {
            color: $color-gray-80;
            pointer-events: none;
        }

        .date-tooltip-wide {
            --tooltip-width: #{px-to-rem(140px)};
        }

        .date-tooltip-narrow {
            --tooltip-width: #{px-to-rem(70px)};
        }
    }

    [dir=rtl] {
        .active-column-value {
            margin: 0 0 0 $spacing-200;
        }

        .active-column-number {
            text-align: left;
            padding: 0 0 0 $spacing-100;
        }
    }
</style>

<style lang="scss" rel="stylesheet/scss">
    // TODO: There is an issue with the design system table component
    // that does not allow the selectAll prop to disable/enable the selectAll
    // checkbox. This will hide it for now since we want it disabled.
    .table-grid .table-wrapper thead .selectAll.checkbox {
        display: none;
    }
</style>

<i18n>
{
    "en-us": {
        "campaignArchive": "Campaign archived",
        "undo": "Undo"
    }
}
</i18n>
